import axios, { AxiosResponse } from 'axios';
import { Config } from '../config';

// Types for the frontend implementation
export interface Coordinates {
  x: number;
  y: number;
  imageWidth: number;
  imageHeight: number;
  zoomLevel: number;
  rotation: number;
}

export interface DefectPin {
  id: string;
  coordinates: Coordinates;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  assignedTo?: string;
  assignedToName?: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  photos: string[];
  trade?: string;
  category?: string;
  drawingId: string;
  projectId: string;
  zoneId?: string;
  zoneName?: string;
  markerType?: 'defect' | 'note' | 'measurement' | 'inspection';
  customIcon?: string;
  isUrgent?: boolean;
  dueDate?: string;
  estimatedDuration?: number; // in minutes
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// API Configuration
const API_BASE_URL = Config.apiUrl;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
});

// Request interceptor for auth tokens
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      // Redirect to login or show error
      console.warn('Authentication required');
    }
    return Promise.reject(error);
  }
);

// Defects API functions
export const defectsApi = {
  // Get defects for a specific drawing
  async getDefects(drawingId: string, projectId?: string): Promise<DefectPin[]> {
    try {
      const response: AxiosResponse<ApiResponse<DefectPin[]>> = await api.get(`/defects`, {
        params: { drawingId, projectId }
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch defects:', error);
      throw new Error(`Failed to fetch defects: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Create new defect
  async createDefect(defectData: Partial<DefectPin>): Promise<DefectPin> {
    try {
      const response: AxiosResponse<ApiResponse<DefectPin>> = await api.post('/defects', defectData);
      return response.data.data;
    } catch (error) {
      console.error('Failed to create defect:', error);
      throw new Error(`Failed to create defect: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Update defect
  async updateDefect(defectId: string, defectData: Partial<DefectPin>): Promise<DefectPin> {
    try {
      const response: AxiosResponse<ApiResponse<DefectPin>> = await api.put(`/defects/${defectId}`, defectData);
      return response.data.data;
    } catch (error) {
      console.error('Failed to update defect:', error);
      throw new Error(`Failed to update defect: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Delete defect
  async deleteDefect(defectId: string): Promise<void> {
    try {
      await api.delete(`/defects/${defectId}`);
    } catch (error) {
      console.error('Failed to delete defect:', error);
      throw new Error(`Failed to delete defect: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Upload photo
  async uploadPhoto(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response: AxiosResponse<ApiResponse<{ url: string }>> = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.data.url;
    } catch (error) {
      console.error('Failed to upload photo:', error);
      // Return mock URL for development
      return URL.createObjectURL(file);
    }
  },

  // Get available trades
  async getTrades(): Promise<string[]> {
    try {
      const response: AxiosResponse<ApiResponse<string[]>> = await api.get('/defects/trades');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch trades:', error);
      throw new Error(`Failed to fetch trades: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get available categories
  async getCategories(): Promise<string[]> {
    try {
      const response: AxiosResponse<ApiResponse<string[]>> = await api.get('/defects/categories');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      throw new Error(`Failed to fetch categories: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get available assignees
  async getAssignees(projectId?: string): Promise<{ id: string; name: string; email: string }[]> {
    try {
      const response = await api.get('/defects/assignees', {
        params: { projectId }
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch assignees:', error);
      throw new Error(`Failed to fetch assignees: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
};

// Utility functions for data validation and formatting
export const validateDefectData = (defectData: Partial<DefectPin>): string[] => {
  const errors: string[] = [];
  
  if (!defectData.title || defectData.title.trim().length === 0) {
    errors.push('Title is required');
  }
  
  if (!defectData.description || defectData.description.trim().length === 0) {
    errors.push('Description is required');
  }
  
  if (!defectData.coordinates) {
    errors.push('Coordinates are required');
  } else {
    const { x, y } = defectData.coordinates;
    if (x < 0 || x > 1 || y < 0 || y > 1) {
      errors.push('Coordinates must be between 0 and 1');
    }
  }
  
  if (!defectData.drawingId) {
    errors.push('Drawing ID is required');
  }
  
  if (!defectData.projectId) {
    errors.push('Project ID is required');
  }
  
  return errors;
};

export const formatDefectForBackend = (defectData: Partial<DefectPin>): any => {
  return {
    title: defectData.title?.trim(),
    description: defectData.description?.trim(),
    severity: defectData.severity || 'medium',
    status: defectData.status || 'open',
    coordinates: defectData.coordinates,
    assignedTo: defectData.assignedTo,
    trade: defectData.trade,
    category: defectData.category,
    drawingId: defectData.drawingId,
    projectId: defectData.projectId,
    zoneId: defectData.zoneId,
    markerType: defectData.markerType || 'defect',
    customIcon: defectData.customIcon,
    isUrgent: defectData.isUrgent || false,
    dueDate: defectData.dueDate,
    estimatedDuration: defectData.estimatedDuration,
    photos: defectData.photos || []
  };
};

export default defectsApi;
